import type { Type } from "@angular/core";
import { HomeDashPanelAvailableSpaceComponent } from "./available-space/home.dash.panel.available.space.component";
import { HomeDashPanelExpressSchedulingComponent } from "./express-scheduling/home.dash.panel.express.scheduling.component";
import { HomeDashPanelListComponent } from "./list/home.dash.panel.list.component";
import { HomeDashPanelQuickSearchComponent } from "./quick-search/home.dash.panel.quick.search.component";
import type { HomeDashCustomize, HomeDashPanelModel } from "./home.dash.panel.model";
import type { HomeDashPanelHeadChrome } from "./head/home.dash.panel.head.state";

/** Reactive shell state passed into registry input factories. */
export type HomeDashPanelBodyContext = {
    panel: HomeDashPanelModel;
    viewModel: HomeDashPanelModel;
    isToolbox: boolean;
    customize: HomeDashCustomize | undefined;
    refreshTick: number;
    headBackTick: number;
    isSortable: boolean;
};

/** Shell hooks used when wiring body outputs (stable; not re-read on every input tick). */
export type HomeDashPanelBodyOutputShell = {
    onHeadChromeChange: (chrome: HomeDashPanelHeadChrome) => void;
};

export type HomeDashPanelBodyDefinition = {
    component: Type<unknown>;
    inputs: (context: HomeDashPanelBodyContext) => Record<string, unknown>;
    outputs?: (shell: HomeDashPanelBodyOutputShell) => Record<string, (value: unknown) => void>;
};

const listBody: HomeDashPanelBodyDefinition = {
    component: HomeDashPanelListComponent,
    inputs: (context) => ({
        model: context.viewModel,
        isToolbox: context.isToolbox,
        customize: context.customize,
        refreshTick: context.refreshTick,
    }),
};

const quickSearchBody: HomeDashPanelBodyDefinition = {
    component: HomeDashPanelQuickSearchComponent,
    inputs: (context) => ({
        model: context.viewModel,
        isToolbox: context.isToolbox,
        customize: context.customize,
        refreshTick: context.refreshTick,
    }),
};

const availableSpaceBody: HomeDashPanelBodyDefinition = {
    component: HomeDashPanelAvailableSpaceComponent,
    inputs: (context) => ({
        panel: context.viewModel,
        isToolbox: context.isToolbox,
        customize: context.customize,
        refreshTick: context.refreshTick,
        headBackTick: context.headBackTick,
    }),
    outputs: (shell) => ({
        headChromeChange: (chrome) => shell.onHeadChromeChange(chrome as HomeDashPanelHeadChrome),
    }),
};

const expressScheduleBody: HomeDashPanelBodyDefinition = {
    component: HomeDashPanelExpressSchedulingComponent,
    inputs: (context) => ({
        panel: context.panel,
        isToolbox: context.isToolbox,
        customize: context.customize,
        formOnly: false,
        refreshTick: context.refreshTick,
    }),
};

const openEventFormBody: HomeDashPanelBodyDefinition = {
    component: HomeDashPanelExpressSchedulingComponent,
    inputs: (context) => ({
        panel: context.panel,
        isToolbox: context.isToolbox,
        customize: context.customize,
        keyboardSortable: context.isSortable,
        formOnly: true,
        refreshTick: context.refreshTick,
    }),
};

const BODIES_BY_KIND: Record<string, HomeDashPanelBodyDefinition> = {
    list: listBody,
    quick_search: quickSearchBody,
    available_space: availableSpaceBody,
    express_schedule: expressScheduleBody,
    open_event_form: openEventFormBody,
};

/** Resolve the body component for a dash {@link DashLangMap} panel kind. Unknown kinds use the list body. */
export function resolveHomeDashPanelBody(kind: string | undefined): HomeDashPanelBodyDefinition {
    if (!kind) {
        return listBody;
    }
    return BODIES_BY_KIND[kind] ?? listBody;
}
